import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, IconButton,
  InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  ReceiptLong as RecipeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuConsumptionRecipeService from '../../../../services/menuConsumptionRecipeService';
import type { MenuConsumptionRecipe } from '../../../../types/menu';

// Phase 4.L — operator-facing language: same wording as the embedded
// MenuRecipesPanel and MenuPreviewPanel so the standalone page doesn't
// stand out with engineering jargon ("storage only", "depletion").
const INERT_BANNER =
  'Recipes saved here can be reused across menus. They are not active for sales or stock yet.';

type ActiveFilter = 'true' | 'false' | 'all';

const thStyle = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
};

const MenuConsumptionRecipeList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const base = `/admin/venues/${venueId}/menu-consumption-recipes`;

  const [recipes, setRecipes] = useState<MenuConsumptionRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('true');
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25, totalItems: 0 });

  const fetchRecipes = useCallback(
    async (active: ActiveFilter, page: number, pageSize: number) => {
      setLoading(true);
      setFetchError('');
      try {
        const result = await menuConsumptionRecipeService.getRecipes(
          venueId,
          page + 1,
          pageSize,
          active,
        );
        setRecipes(result.data);
        setPagination(prev => ({ ...prev, page, pageSize, totalItems: result.pagination.totalItems }));
      } catch {
        setFetchError('Could not load recipes. Check your connection and try again.');
      } finally {
        setLoading(false);
      }
    },
    [venueId],
  );

  useEffect(() => {
    fetchRecipes(activeFilter, pagination.page, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, pagination.page, pagination.pageSize]);

  const handleDelete = async (e: React.MouseEvent, recipe: MenuConsumptionRecipe) => {
    e.stopPropagation();
    if (!window.confirm(`Delete recipe "${recipe.name}"? It can be restored by toggling active in edit mode.`)) return;
    try {
      await menuConsumptionRecipeService.deleteRecipe(venueId, recipe._id);
      fetchRecipes(activeFilter, pagination.page, pagination.pageSize);
    } catch {
      setFetchError(`Could not delete "${recipe.name}". Please try again.`);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Persistent INERT banner — first thing every operator sees. */}
      <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
        {INERT_BANNER}
      </Alert>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RecipeIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Recipes</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`${base}/create`)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            New Recipe
          </Button>
        </Box>

        {/* Fetch error */}
        {fetchError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setFetchError('')} sx={{ borderRadius: 1 }}>{fetchError}</Alert>
          </Box>
        )}

        {/* Filters */}
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Active</InputLabel>
            <Select
              value={activeFilter}
              label="Active"
              onChange={e => {
                setActiveFilter(e.target.value as ActiveFilter);
                setPagination(p => ({ ...p, page: 0 }));
              }}
            >
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive (soft-deleted)</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Name</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }} align="right"># Components</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Active</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Updated</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : recipes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <RecipeIcon sx={{ fontSize: 44, mb: 1.5, opacity: 0.25 }} />
                      <Typography variant="body2" fontWeight={500} gutterBottom>
                        {activeFilter === 'false' ? 'No inactive recipes.' : 'No recipes yet.'}
                      </Typography>
                      {activeFilter !== 'false' && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => navigate(`${base}/create`)}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          Create first recipe
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                recipes.map(recipe => (
                  <TableRow
                    key={recipe._id}
                    hover
                    onClick={() => navigate(`${base}/edit/${recipe._id}`)}
                    sx={{ cursor: 'pointer', opacity: recipe.active ? 1 : 0.55 }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{recipe.name}</Typography>
                      {recipe.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {recipe.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {recipe.components?.length ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={recipe.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={recipe.active ? 'success' : 'default'}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {recipe.updatedAt ? new Date(recipe.updatedAt).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Tooltip title="Edit recipe">
                        <IconButton size="small" onClick={() => navigate(`${base}/edit/${recipe._id}`)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {recipe.active && (
                        <Tooltip title="Soft-delete (active=false)">
                          <IconButton size="small" color="error" onClick={e => handleDelete(e, recipe)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.totalItems}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          onPageChange={(_e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          onRowsPerPageChange={e => setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Box>
  );
};

export default MenuConsumptionRecipeList;
